/**
 * Minimal GraphQL fetcher.
 *
 * AppSync answers HTTP 200 with an `errors` array, so a plain `response.ok` check passes on a failed
 * query — the error array has to be inspected explicitly or failures show up as blank screens.
 */
export interface GraphQLClient {
  endpoint: string;
  getIdToken: () => Promise<string>;
}

export class GraphQLError extends Error {
  constructor(
    message: string,
    readonly errorType?: string,
  ) {
    super(message);
    this.name = 'GraphQLError';
  }
}

export const request = async <T>(
  client: GraphQLClient,
  query: string,
  variables?: Record<string, unknown>,
): Promise<T> => {
  const token = await client.getIdToken();

  const response = await fetch(client.endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!response.ok) {
    throw new GraphQLError(`AppSync returned HTTP ${response.status}`);
  }

  const body = (await response.json()) as {
    data?: T;
    errors?: { message: string; errorType?: string }[];
  };

  if (body.errors?.length) {
    const first = body.errors[0];
    throw new GraphQLError(
      body.errors.map((e) => e.message).join('; '),
      first.errorType,
    );
  }

  if (!body.data) {
    throw new GraphQLError('AppSync returned no data');
  }

  return body.data;
};
